import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './assign-portal-accounts.scss';
import { DataTable, Form } from '@ampol/design-system';
import { getAccountLevelName } from 'utilities/core-utilities';
import { getAccountsForUser, getAccountsForUserReset } from 'actions/accountActions';
import {
  assignAccountsToUser,
  updateAdminAssignAccountsToUserRequest,
  assignAccountsToUserReset,
} from 'actions/userActions';
import { getAssignAccountsUserMap } from 'utilities/user-utilities';
import { setAdminAssignUsersButton } from 'actions/modalMetaActions';
import Regex from 'constants/regex';
import SearchInput from 'components/search-input/search-input';
import * as yup from 'yup';
import ContentControls from 'components/content-controls/content-controls';
import Env from 'Env';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Admin Portal - Users Modal - Assign Portal Accounts
 * @returns JSX Element
 */
const AssignPortalAccounts = ({ userId, onAssign }) => {
  const dispatch = useDispatch();
  const assignRolesList = useSelector((state) => state.assignRolesList.roles);

  const getAccountsForUserLoading = useSelector((state) => state.accountsStore.getAccountsForUserLoading);
  const getAccountsForUserResult = useSelector((state) => state.accountsStore.getAccountsForUserResult);
  const assignAccountsRequest = useSelector((state) => state.userStore.assignAccountsRequest);
  const getAccountsForUserError = useSelector((state) => state.accountsStore.getAccountsForUserError);

  const assignAccountsToUserLoaded = useSelector((state) => state.userStore.assignAccountsToUserLoaded);

  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const [sortColumn, setSortColumn] = useState('accountNumber');
  const [sortDirection, setSortDirection] = useState('desc');

  const [parentAccount, setParentAccount] = useState(null);
  const [grandParentAccount, setGrandParentAccount] = useState(null);
  const [assignedAncestor, setAssignedAncestor] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

  const [accountNumber, setAccountNumber] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 5;
  const totalPages = Math.ceil(getAccountsForUserResult?.resultHierarchySize / pageSize);

  const validAccountRegex = Env.isAmpolCashEnabled() ? Regex.ACCOUNT_NUMBER : Regex.CARD_ACCOUNT;

  const searchSchema = yup.object().shape({
    searchTerm: yup
      .string()
      .nullable()
      .matches(validAccountRegex, { excludeEmptyString: true, message: Validation.ACCOUNT_NUMBER_TEN_DIGITS })
      .test('searchTerm', Validation.ACCOUNT_NUMBER_NOT_FOUND, () => !getAccountsForUserError),
  });

  useEffect(() => {
    dispatch(
      setAdminAssignUsersButton({
        onClick: submitButtonDisabled ? undefined : handleSubmitAdminAssignAccount,
        inactive: submitButtonDisabled,
      })
    );
  }, [submitButtonDisabled, assignAccountsRequest]);

  useEffect(() => {
    dispatch(getAccountsForUserReset());
    return () => {
      dispatch(getAccountsForUserReset());
      dispatch(assignAccountsToUserReset());
      setAccountNumber(null);
    };
  }, []);

  useEffect(() => {
    if (accountNumber) {
      dispatch(getAccountsForUser(userId, accountNumber, pageSize, pageNumber, sortColumn, sortDirection));
    }
  }, [accountNumber, pageNumber, sortColumn, sortDirection]);

  useEffect(() => {
    if (getAccountsForUserResult) {
      const grandParent = getAccountsForUserResult.ancestors.find((x) => x.level === 'A');
      const parent = getAccountsForUserResult.ancestors.find((x) => x.level === 'B');

      setGrandParentAccount(grandParent);
      setParentAccount(parent);
      setSelectedRole(getAccountsForUserResult.assignedRoleId ? getAccountsForUserResult.assignedRoleId : selectedRole);

      if (grandParent?.isSelected) {
        setAssignedAncestor(grandParent);
      } else if (parent?.isSelected) {
        setAssignedAncestor(parent);
      } else {
        setAssignedAncestor(null);
      }

      dispatch(
        updateAdminAssignAccountsToUserRequest({
          grandParentCustomerNumber: grandParent
            ? grandParent.accountNumber
            : parent
              ? parent.accountNumber
              : getAccountsForUserResult.result.accountNumber,
          userId: userId,
          assignAccountsUserMap: getAssignAccountsUserMap(getAccountsForUserResult.result, grandParent, parent),
          assignedClientRoles: [
            {
              grandfatherClientNumber: grandParent
                ? grandParent.accountNumber
                : parent
                  ? parent.accountNumber
                  : getAccountsForUserResult.result.accountNumber,
              // accountName: grandParent
              //   ? grandParent.accountName
              //   : parent ? parent.accountName : getAccountsForUserResult.result.accountName,
              assignedRoleIds: [getAccountsForUserResult.assignedRoleId],
            },
          ],
        })
      );
    } else {
      setGrandParentAccount(null);
      setParentAccount(null);
      setAssignedAncestor(null);
    }
  }, [getAccountsForUserResult]);

  useEffect(() => {
    const assignButtonEnabled = selectedRole && getAccountsForUserResult?.result;
    setSubmitButtonDisabled(!assignButtonEnabled);

    if (assignButtonEnabled) {
      dispatch(
        updateAdminAssignAccountsToUserRequest({
          ...assignAccountsRequest,
          assignedClientRoles: [
            {
              ...assignAccountsRequest?.assignedClientRoles?.[0],
              assignedRoleIds: [selectedRole],
            },
          ],
        })
      );
    }
  }, [selectedRole, getAccountsForUserResult]);

  useEffect(() => {
    if (assignAccountsToUserLoaded) {
      onAssign();
    }
  }, [assignAccountsToUserLoaded]);

  const handleSubmitAdminAssignAccount = () => {
    setSubmitButtonDisabled(true);
    dispatch(
      assignAccountsToUser(
        assignAccountsRequest?.grandParentCustomerNumber,
        assignAccountsRequest?.userId,
        assignAccountsRequest?.assignAccountsUserMap,
        null,
        assignAccountsRequest?.assignedClientRoles
      )
    );

    trackCustomEvent(
      'Submit Assign Account',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Assign in Admin User Detail Modal to Assign Portal Accounts.',
      { selectedUserId: userId }
    );
  };

  const onRoleChange = (values) => {
    setSelectedRole(values.role);
  };

  const onPageNumberChange = (value) => {
    setPageNumber(value);
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of admin account assignment.'
    );
  };

  const onTableColumnSorted = (column, isAscending) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  };

  const rowClassCondition = (row) => {
    return row.level === 'A'
      ? 'assign-portal-accounts__row-grand-parent'
      : row.level === 'B'
        ? 'assign-portal-accounts__row-parent'
        : '';
  };

  const handleSearch = (value) => {
    if (value.match(validAccountRegex)) {
      setPageNumber(1);
      setAccountNumber(value);
    } else {
      setAccountNumber(null);
      setSelectedRole(null);
      dispatch(getAccountsForUserReset());
    }
  };

  return (
    <div>
      <div className='assign-portal-accounts__account-form'>
        <div className='assign-portal-accounts__search-input'>
          <SearchInput
            onSearch={handleSearch}
            searchSchema={searchSchema}
            triggerValidation={getAccountsForUserError}
            className={'custom-width'}
          />
        </div>
        <div className='assign-portal-accounts__role-dropdown'>
          <Form onChange={onRoleChange} defaultValues={{ role: selectedRole }} schema={{}}>
            <Form.DropDown
              name='role'
              label='Security role'
              placeHolder='Select role'
              options={assignRolesList.map((x) => ({ label: x.name, value: x.id }))}
              ariaLabel={'Select role'}
              disabled={!getAccountsForUserResult}
            />
          </Form>
        </div>
      </div>

      {grandParentAccount && !parentAccount && assignedAncestor?.isSelected && (
        <div className='assign-portal-accounts__warning-message'>
          The selected account is part of hierarchy with GrandParent {grandParentAccount.accountNumber} and has access
          to the selected account with {getAccountsForUserResult?.assignedRoleName} role. Assigning another role will
          override the role for all the accounts under the GrandParent Account {grandParentAccount.accountNumber}{' '}
          hierarchy.
        </div>
      )}

      {grandParentAccount &&
        !parentAccount &&
        !assignedAncestor &&
        getAccountsForUserResult?.hasExistingAccessInHierarchy && (
        <div className='assign-portal-accounts__warning-message'>
            The selected account is part of hierarchy with GrandParent {grandParentAccount.accountNumber} and has access
            to other Parent/Child accounts. Assigning another role will override the role for all the other accounts
            where the user has access.
        </div>
      )}

      {grandParentAccount && parentAccount && assignedAncestor?.isSelected && (
        <div className='assign-portal-accounts__warning-message'>
          The selected account is part of hierarchy with {assignedAncestor.level === 'A' ? 'GrandParent' : 'Parent'}{' '}
          {assignedAncestor.accountNumber} and has access to the selected account with{' '}
          {getAccountsForUserResult?.assignedRoleName} role. Assigning another role will override the role for all the
          accounts under the {assignedAncestor.level === 'A' ? 'GrandParent' : 'Parent'} Account{' '}
          {assignedAncestor.accountNumber} hierarchy.
        </div>
      )}

      {grandParentAccount &&
        parentAccount &&
        !assignedAncestor &&
        getAccountsForUserResult?.hasExistingAccessInHierarchy && (
        <div className='assign-portal-accounts__warning-message'>
            The selected account is part of hierarchy with GrandParent {grandParentAccount.accountNumber} and has access
            to other Parent and/or Child accounts with {getAccountsForUserResult?.assignedRoleName} role. Assigning
            another role will override the role for all the other Parent/Child accounts under the GrandParent Account{' '}
          {grandParentAccount.accountNumber} hierarchy where the user has access.
        </div>
      )}

      {totalPages > 1 && (
        <ContentControls
          showPageControls={true}
          pageNumber={pageNumber}
          pageTotal={totalPages}
          onPageChange={onPageNumberChange}
          itemCount={getAccountsForUserResult?.resultHierarchySize}
        />
      )}
      <div className={'assign-portal-accounts__table-container'}>
        <DataTable
          noDataMessage={'Search by account number to show results'}
          caption={'Assign Portal Accounts'}
          columns={[
            {
              header: 'Account Number',
              accessorKey: 'accountNumber',
              defaultSorted: 'desc',
            },
            {
              header: 'Account Name',
              accessorKey: 'accountName',
            },
            {
              header: 'Level',
              id: 'accountLevel',
              accessorFn: (x) => getAccountLevelName(x.level),
              disableSort: true,
            },
          ]}
          isSortable={true}
          onColumnSorted={onTableColumnSorted}
          isLoading={getAccountsForUserLoading}
          columnTemplate={'1fr 1fr 1fr'}
          data={getAccountsForUserResult?.resultHierarchy}
          tableVariant='list'
          loadingRowCount={pageSize}
          rowClassFn={(row) => rowClassCondition(row)}
          isSelectable={true}
          rowSelectionDisabledFn={() => true}
          rowSelectedFn={() => true}
        />
      </div>
    </div>
  );
};

export default AssignPortalAccounts;
